/* Rapport d'inspection — fiche produit (plan 2026-08-27, révision v2).

   Patron : assets/product-condition-modal.js (référence pour tout ce qui touche le
   <dialog>) et assets/product-price-detail.js — IIFE, garde d'attachement unique,
   délégation des CLICS sur `document` (les clics bullent, donc les listeners survivent
   au re-render de section dans l'éditeur sans réattachement).

   TROIS choses ne peuvent PAS être déléguées, et sont traitées à part :

   1. l'event `close` d'un <dialog> NE BULLE PAS — et `cancel` non plus (finding H3).
      La v1 déléguait `cancel` sur `document` : c'était du CODE MORT, donc après Esc le
      focus n'était jamais rendu au déclencheur, alors que la DoD cochait cette case.
      Le repo l'a déjà payé et documenté : product-condition-modal.js:76 « Câblage
      paresseux de 'close' (cet event NE BULLE PAS, donc indélégable) » et
      product-price-detail.js:134 « les clics bullent, contrairement à 'close'/'cancel' ».
      → câblage PARESSEUX PAR NŒUD sur `close`, drapeau porté par le nœud.

   2. l'état du déclencheur (finding L9). Il est porté PAR LE NŒUD
      (`dialog.__certificateTrigger`) et jamais en variable de module : après un
      re-render de section, une variable de module pointerait un nœud détaché et
      `focus()` ne ferait rien, en silence. Même raison que
      product-condition-modal.js:6-9.

   3. l'IntersectionObserver de l'event de vue observe un nœud précis, détruit au
      re-render → réarmement sur `shopify:section:load`, FILTRÉ sur la section
      concernée (finding L6). */
(function () {
  'use strict';

  if (window.__inspectionBound) return;
  window.__inspectionBound = true;

  var DIALOG = 'product-certificate';
  var FALLBACK = 'product-certificate--fallback';

  /* --------------------------------------------------------------------------
     Verrou de scroll (finding M3) — copie du patron product-condition-modal.js:26-68,
     iOS-safe. Les DEUX modales canoniques du thème en ont un, et pour la même raison :
     en mobile la modale est plein écran et `overscroll-behavior` ne couvre que le
     panneau — tout geste sur les gouttières scrollait la page derrière. Ici le cas est
     pire : le document du certificat est long (58 points de contrôle groupés), donc le
     panneau scrolle beaucoup et chaque geste qui rate sa boîte atteint la page.
     -------------------------------------------------------------------------- */
  var savedScrollY = 0;
  var savedBody = null;

  function lockScroll() {
    // Idempotent : re-sauver l'état alors qu'il est déjà verrouillé capturerait
    // `position: fixed` et casserait la restauration.
    if (savedBody) return;
    savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    var b = document.body;
    savedBody = {
      position: b.style.position,
      top: b.style.top,
      left: b.style.left,
      right: b.style.right,
      width: b.style.width,
      overflow: b.style.overflow,
    };
    b.style.position = 'fixed';
    b.style.top = '-' + savedScrollY + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (!savedBody) return;
    var b = document.body;
    var st = savedBody;
    b.style.position = st.position || '';
    b.style.top = st.top || '';
    b.style.left = st.left || '';
    b.style.right = st.right || '';
    b.style.width = st.width || '';
    b.style.overflow = st.overflow || '';
    savedBody = null;

    // `mint.css` pose `scroll-behavior: smooth` sur `html` : sans neutralisation, la remise
    // en place est ANIMEE depuis le haut de page — la page remonte puis redescend vers le
    // bloc d'inspection sous les yeux du lecteur (signale le 2026-09-07, anti-pattern n°31).
    // On coupe le temps du saut plutot que de compter sur `behavior: 'instant'`, moins bien
    // supporte sur les Safari anciens. Meme parade que `greenleaze-location-modal.js`.
    var html = document.documentElement;
    var previousBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, savedScrollY);
    html.style.scrollBehavior = previousBehavior;
  }

  function pushEvent(name, data) {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, data);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, data));
    }
  }

  /* --------------------------------------------------------------------------
     Impression du certificat.

     Le document est du HTML rendu côté serveur — plus de PDF téléversé, donc plus de
     <template> à cloner ni de requête réseau à différer. « Télécharger le PDF » appelle
     `window.print()`, et le navigateur offre « Enregistrer au format PDF » (sur iOS,
     « PDF » depuis la feuille de partage de l'aperçu d'impression).

     Pas de librairie : html2canvas rasterise (PDF de plusieurs Mo, texte non
     sélectionnable, webfonts du thème qui trébuchent) et jsPDF avec placement manuel
     dupliquerait la mise en page dans un second jeu de coordonnées, pour un document
     dont 95 % du contenu est commun à tout le catalogue.

     Le document est DÉPLACÉ hors du dialog le temps du print : un dialog modal vit dans
     le top layer, dont le rendu à l'impression diffère selon les navigateurs. Le nœud
     revient à sa place dans `afterprint`, et aussi immédiatement après `print()` — sur
     certains navigateurs `print()` est synchrone et `afterprint` peut ne pas être émis.
     La remise en place est idempotente, la doubler ne coûte rien et évite de laisser le
     certificat orphelin au milieu de la page.
     -------------------------------------------------------------------------- */
  var PRINT_HOST_ID = 'MintCertificatePrintHost';
  var PRINT_FLAG = 'mint-printing-certificate';

  function printHost() {
    var host = document.getElementById(PRINT_HOST_ID);
    if (!host) {
      host = document.createElement('div');
      host.id = PRINT_HOST_ID;
      document.body.appendChild(host);
    }
    return host;
  }

  function restoreDoc(state) {
    if (!state || !state.doc || !state.parent) return;
    if (state.doc.parentNode === state.parent) return;
    if (state.next && state.next.parentNode === state.parent) {
      state.parent.insertBefore(state.doc, state.next);
    } else {
      state.parent.appendChild(state.doc);
    }
    document.documentElement.classList.remove(PRINT_FLAG);
  }

  /**
   * Ouvre le certificat dans un nouvel onglet plutot que dans la modale.
   *
   * On CLONE `[data-certificate-doc]`, on ne le deplace pas : `printCertificate` le sort deja
   * du dialog pour imprimer et le remet en place ensuite (cf. son commentaire) — deux
   * mecanismes qui deplacent le meme noeud finiraient par se le voler.
   *
   * Les feuilles de la page sont recopiees telles quelles : le certificat n'a pas de styles
   * propres, il herite de ceux du theme. Sans elles l'onglet affiche du texte nu.
   *
   * Le document part en URL `blob:` et non en `document.write` dans `about:blank` : Edge
   * Android ignore le meta viewport écrit après coup et affiche la page dézoomée au quart.
   * Le `<base>` résout les URL relatives et `//cdn…`, qu'une URL `blob:` ne sait pas résoudre.
   *
   * Rend `false` si l'ouverture echoue (bloqueur de pop-up, ou document introuvable) pour que
   * l'appelant retombe sur la modale.
   */
  function ouvrirCertificatDansOnglet(root) {
    var doc = root ? root.querySelector('[data-certificate-doc]') : null;
    if (!doc || typeof window.open !== 'function' || typeof URL.createObjectURL !== 'function') return false;

    var feuilles = [];
    var noeuds = document.querySelectorAll('link[rel="stylesheet"], style');
    for (var i = 0; i < noeuds.length; i++) feuilles.push(noeuds[i].outerHTML);

    var langue = document.documentElement.getAttribute('lang') || 'fr';
    var titre = (document.title || 'Certificat').replace(/[<>]/g, '');
    var base = document.baseURI.replace(/"/g, '&quot;');

    var html =
      '<!doctype html><html lang="' +
      langue +
      '"><head><meta charset="utf-8">' +
      '<meta name="viewport" content="width=device-width,initial-scale=1">' +
      '<base href="' +
      base +
      '">' +
      '<title>' +
      titre +
      '</title>' +
      feuilles.join('') +
      '<style>html,body{background:#fff}body{margin:0;padding:24px}</style>' +
      '</head><body>' +
      doc.outerHTML +
      '</body></html>';

    /* Jamais révoquée : l'onglet doit tenir un rechargement, et le blob meurt avec la fiche. */
    var url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    var onglet;
    try {
      onglet = window.open(url, '_blank');
    } catch (err) {
      onglet = null;
    }
    if (!onglet) {
      URL.revokeObjectURL(url);
      return false;
    }
    return true;
  }

  function printCertificate(dialog) {
    if (!dialog) return;
    var doc = dialog.querySelector('[data-certificate-doc]');
    if (!doc || typeof window.print !== 'function') return;

    var state = { doc: doc, parent: doc.parentNode, next: doc.nextElementSibling };
    dialog.__certificatePrintState = state;
    printHost().appendChild(doc);
    document.documentElement.classList.add(PRINT_FLAG);

    var done = function () {
      window.removeEventListener('afterprint', done);
      restoreDoc(state);
    };

    /* Ne JAMAIS remettre le nœud en place juste après `print()` : dans Chrome, `print()`
       n'est pas bloquant — il ouvre l'aperçu et le JS continue. Un `finally` qui restaure
       enlevait donc la classe et sortait le document du hôte AVANT que l'aperçu ne soit
       rendu, et l'impression sortait une page vide. Mesuré le 2026-09-04.
       `afterprint` est émis à la fermeture de l'aperçu ; la branche `else` couvre le cas
       exotique d'un navigateur sans cet event, où `print()` est alors bloquant. */
    if ('onafterprint' in window) {
      window.addEventListener('afterprint', done);
      window.print();
    } else {
      window.print();
      done();
    }
  }

  /* Câblage paresseux de 'close' (finding H3). Le drapeau est posé SUR LE NŒUD : un
     re-render crée un dialog vierge, recâblé à sa première ouverture. Cet unique
     listener couvre Esc natif ET tout appel à `close()`. */
  function wireDialog(dialog) {
    if (dialog.__certificateWired) return;
    dialog.__certificateWired = true;
    dialog.addEventListener('close', function () {
      onClosed(dialog);
    });
  }

  /* Émis pour les deux modes d'ouverture : l'onglet, mode par défaut, ne passe pas par
     openDialog(). Le handle vit sur `.product-section[data-product-handle]` (cf. theme.js),
     jamais sur documentElement. `cta_name` est lu sur le déclencheur : le tracker générique
     ignore tout élément qui porte cet attribut (tracking-ui-forms.liquid). */
  function trackCertificateOpen(dialog, trigger) {
    var holder = document.querySelector('.product-section[data-product-handle]');
    pushEvent('certificate_modal_open', {
      cta_location: (dialog && dialog.getAttribute('data-cta-location')) || 'certificate_modal',
      cta_name: (trigger && trigger.getAttribute('data-cta-name')) || 'inspection_certificate',
      product_handle: holder ? holder.getAttribute('data-product-handle') : '',
    });
  }

  function openDialog(dialog, trigger) {
    if (!dialog) return;
    if (dialog.__certificateOpen) return;

    /* Filet : si `afterprint` n'a pas été émis (navigateur exotique, aperçu fermé d'une
       manière que l'event ne couvre pas), le document serait resté dans le hôte
       d'impression et la modale se réouvrirait VIDE — mesuré le 2026-09-04 en simulant un
       afterprint manquant. La restauration à l'ouverture couvre tous les chemins, et
       `restoreDoc` est idempotent : elle sort si le nœud est déjà en place. */
    restoreDoc(dialog.__certificatePrintState);

    // État porté PAR LE NŒUD (finding L9), jamais en variable de module.
    dialog.__certificateTrigger = trigger || null;
    wireDialog(dialog);

    if (typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
      } catch (e) {
        openFallback(dialog);
      }
    } else {
      openFallback(dialog);
    }
    dialog.__certificateOpen = true;
    lockScroll();

    var title = dialog.querySelector('[id^="CertificateTitle"]') || dialog.querySelector('[tabindex="-1"]');
    if (title && typeof title.focus === 'function') title.focus();

    trackCertificateOpen(dialog, trigger);
  }

  /* Repli quand `showModal()` est absent : ni top layer ni ::backdrop natifs → le CSS
     sort le dialog du flux via `.product-certificate--fallback[open]`, règle AJOUTÉE en
     T2 (finding M4 : la v1 posait cette classe sans jamais la définir). */
  function openFallback(dialog) {
    dialog.classList.add(FALLBACK);
    dialog.setAttribute('open', '');
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.__certificateOpen) return;
    if (dialog.classList.contains(FALLBACK)) {
      dialog.removeAttribute('open');
      dialog.classList.remove(FALLBACK);
      onClosed(dialog);
    } else if (typeof dialog.close === 'function') {
      dialog.close(); // → event 'close' → onClosed (câblé par wireDialog)
    } else {
      dialog.removeAttribute('open');
      onClosed(dialog);
    }
  }

  /* Idempotent : couvre Esc natif, close(), la croix, le clic backdrop et le repli.
     C'est le SEUL endroit qui déverrouille le scroll et rend le focus — la v1 avait
     ces deux effets dispersés dans deux chemins dont l'un était mort. */
  function onClosed(dialog) {
    if (!dialog || !dialog.__certificateOpen) return;
    dialog.__certificateOpen = false;
    /* Le document revient dans la modale À LA FERMETURE aussi, pas seulement à
       `afterprint` et à la réouverture. Dans une webview in-app où `window.print()` est
       inerte, `afterprint` n'arrive jamais : le nœud restait dans l'hôte d'impression,
       enfant de `body`, et le client fermait la modale sur un certificat empilé en bas de
       la fiche. Le CSS pose désormais aussi un `display: none` d'écran sur l'hôte — les
       deux filets sont voulus, celui-ci rend le DOM propre, l'autre couvre le cas où ce
       chemin n'est pas emprunté. Review du 2026-09-07. */
    restoreDoc(dialog.__certificatePrintState);
    unlockScroll();
    var trigger = dialog.__certificateTrigger;
    dialog.__certificateTrigger = null;
    // Déclencheur resté dans un tiroir fermé ([data-drawer-dismiss], product-drawer.js) : il désigne où rendre le focus.
    if (trigger && trigger.__returnFocus) trigger = trigger.__returnFocus;
    /* `preventScroll` n'est pas cosmetique : rendre le focus au CTA le fait AUSSI defiler
       dans la vue, animation `scroll-behavior: smooth` comprise. Mesure du 2026-09-07 :
       apres avoir neutralise le smooth dans `unlockScroll`, la fermeture ramenait encore la
       page a 4785 au lieu des 4228 sauves, en 19 positions intermediaires — ce n'etait plus
       la restauration mais le focus. Les deux corrections sont necessaires. */
    if (trigger && typeof trigger.focus === 'function') {
      try {
        trigger.focus({ preventScroll: true });
      } catch (err) {
        trigger.focus();
      }
    }
  }

  document.addEventListener('click', function (e) {
    /* Garde : un clic synthétique dont la cible n'est pas un Element (dispatch
       programmatique sur document, courant chez les scripts tiers) ferait lever
       closest() et tuerait TOUT ce handler — ouverture, croix et backdrop compris.
       Patron product-condition-modal.js:165. */
    if (!e.target || !e.target.closest) return;

    var opener = e.target.closest('[data-certificate-open]');
    if (opener) {
      /* Le déclencheur peut vivre hors de la section inspection (panneau Reconditionnement du
         bloc sticky) : repli sur l'unique rapport de la page. */
      var root = opener.closest('[data-inspection-root]') || document.querySelector('[data-inspection-root]');
      var dialog = root ? root.querySelector('[data-certificate-dialog]') : null;
      /* Onglet d'abord, modale en repli. Le repli n'est pas décoratif : `window.open` rend
         `null` dès qu'un bloqueur de pop-up intervient, et un clic qui n'ouvre rien serait pire
         que la modale d'origine. */
      if (ouvrirCertificatDansOnglet(root)) {
        trackCertificateOpen(dialog, opener);
        return;
      }
      openDialog(dialog, opener);
      return;
    }

    var closer = e.target.closest('[data-certificate-close]');
    if (closer) {
      closeDialog(closer.closest('[data-certificate-dialog]'));
      return;
    }

    /* Impression. `data-cta-name` est posé sur ce bouton ET tracé par ce délégué : un
       `data-cta-name` sans délégué dédié n'est JAMAIS tracé, le tracker générique sortant
       dès qu'il le voit (tracking-ui-forms.liquid:198). Le nom d'event remplace
       `certificate_open_pdf`, qui n'a plus d'objet — le périmètre reste à 3 events. */
    var printer = e.target.closest('[data-certificate-print]');
    if (printer) {
      var printDialog = printer.closest('[data-certificate-dialog]');
      pushEvent('certificate_print', {
        cta_location: 'certificate_modal',
        cta_name: printer.getAttribute('data-cta-name') || 'certificate_print',
      });
      printCertificate(printDialog);
      return;
    }

    /* Clic sur le fond : le ::backdrop n'est pas un nœud, donc la cible d'un clic sur
       le voile est le <dialog> lui-même (le panneau interne intercepte les clics de
       contenu). */
    if (e.target.classList && e.target.classList.contains(DIALOG)) {
      closeDialog(e.target);
    }
  });

  /* Esc en mode REPLI uniquement : un <dialog> ouvert par showModal() gère Esc lui-même
     et émet `close`, déjà câblé. En repli (attribut `open` posé à la main) le navigateur
     ne fait rien — sans ce handler, la modale de repli serait inéchappable au clavier.
     Patron product-condition-modal.js:209-213. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    var fallbackOpen = document.querySelector('.' + FALLBACK + '[open]');
    if (fallbackOpen) closeDialog(fallbackOpen);
  });

  /* Event de vue du bloc, une seule fois par page. Verrou global : sur la plage où deux
     rendus pourraient coexister, l'event ne doit pas partir en double (même précaution
     que window.mintVisitorCountViewSent, cf. anti-pattern n°17). */
  function observeView() {
    var root = document.querySelector('[data-inspection-root]');
    if (!root) return;
    if (window.__inspectionViewSent) return;
    if (typeof window.IntersectionObserver !== 'function') return;

    var io = new window.IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          if (window.__inspectionViewSent) {
            io.disconnect();
            return;
          }
          window.__inspectionViewSent = true;
          io.disconnect();
          pushEvent('inspection_view', {
            cta_location: 'product_inspection',
            has_certificate: !!root.querySelector('[data-certificate-dialog]'),
          });
          return;
        }
      },
      { threshold: 0.4 },
    );

    io.observe(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeView);
  } else {
    observeView();
  }

  /* Re-render dans l'éditeur : le nœud observé a été remplacé, il faut ré-observer.
     FILTRÉ sur la section concernée (finding L6) : la v1 remettait le verrou à zéro sur
     CHAQUE `shopify:section:load`, donc `inspection_view` repartait au rechargement de
     n'importe quelle autre section de la page, alors que le bloc n'avait pas bougé.
     T13-14 ne l'aurait pas vu (il ne teste que le déplacement de la section concernée). */
  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target;
    if (!target || typeof target.querySelector !== 'function') return;
    var touched = target.querySelector('[data-inspection-root]');
    if (!touched && target.matches && target.matches('[data-inspection-root]')) {
      touched = target;
    }
    if (!touched) return;
    window.__inspectionViewSent = false;
    observeView();
  });
})();
